import { AsyncPipe } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LucideAngularModule } from 'lucide-angular';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects } from 'radash';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Group } from '../../../models/group.model';
import { SchoolMemberType } from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { UserModel } from '../../../models/user.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { QuizStatusPipe } from '../../../pipes/quizStatus.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../components/buttons/btn-chip/btn-chip.component';
import {
  BtnFabComponent,
  BtnFabPosition,
} from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnIconComponent } from '../../components/buttons/btn-icon/btn-icon.component';
import { BtnMoreComponent } from '../../components/buttons/btn-more/btn-more.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import { AlertType } from '../../components/dialogs/confirm.dialog.model';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';

@Component({
  selector: 'app-quiz-home',
  standalone: true,
  imports: [
    AsyncPipe,
    BtnBackComponent,
    BtnIconComponent,
    BtnRouteComponent,
    LucideAngularModule,
    MomentPipe,
    QuizStatusPipe,
    BtnMoreComponent,
    ChipComponent,
    BtnChipComponent,
    BtnFabComponent,
  ],
  templateUrl: './quiz-home.component.html',
  styleUrl: './quiz-home.component.css',
})
export class QuizHomeComponent implements OnInit, OnDestroy {
  router: Router = inject(Router);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  logger: NGXLogger = inject(NGXLogger);
  quizService = inject(QuizService);
  utilsService = inject(UtilsService);
  toastrService = inject(ToastrService);

  groupId: string | null = null;

  loadingStats$: Observable<boolean> = of(true);
  loadingQuizzes$: Observable<boolean> = of(true);

  group$: Observable<Group | null | undefined> = new Observable();

  private quizzesSubject = new BehaviorSubject<Quiz[]>([]);
  public quizzes$ = this.quizzesSubject.asObservable();

  stats$: Observable<any> = new Observable();
  user: UserModel | null = null;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  chipColor = ChipColor;
  fabPostion = BtnFabPosition;

  myRoles$: Observable<any> = new Observable();
  exit$: Subject<void> = new Subject();

  activeDropdownQuizId = signal<string | null>(null);

  constructor() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.group$ = this.store.select(groupSelector.getGroupById(this.groupId!));

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }

  ngOnInit(): void {
    this.refreshQuizzes();
    this.refreshStats();
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  refreshQuizzes() {
    this.logger.debug('Refreshing quizzes...');
    this.loadingQuizzes$ = of(true);
    try {
      combineLatest([this.myRoles$, this.group$])
        .pipe(take(1))
        .subscribe(([roles, group]) => {
          this.quizService
            .getQuizzes({
              groupId: this.groupId!,
              memberType: (group?.memberType as string[]) || [],
              userId: this.user?.uid || '',
            })
            .subscribe((quizzes) => {
              this.quizzesSubject.next(quizzes);
              this.loadingQuizzes$ = of(false);
            });
        });
    } catch (error) {
      this.logger.error('Error refreshing quizzes:', error);
      this.loadingQuizzes$ = of(false);
    }
  }

  refreshStats() {
    this.logger.debug('Refreshing quiz stats...');
    this.myRoles$.pipe(take(1)).subscribe((roles) => {
      if (roles.management || roles.faculty) {
        this.fetchStats();
      } else {
        this.loadingStats$ = of(false);
      }
    });
  }

  private fetchStats() {
    this.loadingStats$ = of(true);
    try {
      this.stats$ = this.store.select(groupSelector.getSelectedGroupMetadata);
      this.loadingStats$ = of(false);
    } catch (error) {
      this.logger.error('Error refreshing quiz stats:', error);
      this.loadingStats$ = of(false);
    }
  }

  routeQuiz(quiz: Quiz) {
    // this.logger.debug('Routing quiz: ', quiz.title, 'Id: ', quiz.id);
    this.myRoles$.pipe(take(1)).subscribe((roles) => {
      this.logger.debug('User roles in group for routing quiz:', roles);
      this.logger.debug('Quiz status: ', quiz.quizStatus);
      if (
        (quiz.quizStatus.toString() === QuizStatus.draft &&
          this.user?.uid === quiz.createdById &&
          roles.faculty) ||
        roles.management
      ) {
        this.router.navigate([quiz.id], {
          relativeTo: this.activatedRoute,
        });
      } else if (
        quiz.quizStatus.toString() === QuizStatus.scheduled ||
        quiz.quizStatus.toString() === QuizStatus.completed
      ) {
        this.router.navigate([quiz.id], {
          relativeTo: this.activatedRoute,
        });
      } else if (
        quiz.isLive &&
        intersects(
          [QuizStatus.inProgress, QuizStatus.waiting],
          [quiz.quizStatus.toString()],
        )
      ) {
        this.router.navigate([quiz.id], {
          relativeTo: this.activatedRoute,
        });
      }
    });
  }

  addMore() {
    this.logger.debug('Adding more quizzes...');
    this.loadingQuizzes$ = of(true);

    // 1. Get the current state once
    combineLatest([this.myRoles$, this.group$, this.quizzes$])
      .pipe(
        take(1),
        switchMap(([roles, group, currentQuizzes]) => {
          const lastRecord = currentQuizzes[currentQuizzes.length - 1];

          // 2. Fetch the new batch
          return this.quizService
            .getQuizzes({
              groupId: this.groupId!,
              memberType: (group?.memberType as string[]) || [],
              userId: this.user?.uid || '',
              lastRecord: lastRecord?.scheduledAt,
            })
            .pipe(map((newQuizzes) => [...currentQuizzes, ...newQuizzes]));
        }),
      )
      .subscribe({
        next: (combined) => {
          // 4. Push the new combined array back into the BehaviorSubject
          this.quizzesSubject.next(combined);
          this.loadingQuizzes$ = of(false);
        },
        error: (err) => {
          this.logger.error('Error adding quizzes:', err);
          this.loadingQuizzes$ = of(false);
        },
      });
  }

  onCancelQuiz(quiz: Quiz) {
    this.utilsService
      .onRequestConfirmation({
        title: 'Cancel Quiz',
        description: `Are you sure you want to cancel the quiz "${quiz.title}"? This action cannot be undone.`,
        action: 'Cancel',
        showType: AlertType.warning,
        buttonColor: 'bg-red-500',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.quizService
          .updateQuiz(quiz.id, {
            ...quiz,
            quizStatus: QuizStatus.cancelled,
          } as Quiz)
          .then(() => {
            this.logger.debug('Quiz cancelled successfully');
            this.toastrService.success(
              `Quiz "${quiz.title}" cancelled successfully`,
            );
            this.refreshQuizzes();
          })
          .catch((error) => {
            this.logger.error('Error cancelling quiz:', error);
          });
      });
  }

  onCompleteQuiz(quiz: Quiz) {
    this.utilsService
      .onRequestConfirmation({
        title: 'Close Quiz',
        description: `Are you sure you want to close the quiz "${quiz.title}" and compile its analytics? This action cannot be undone.`,
        action: 'Close',
        showType: AlertType.warning,
        buttonColor: 'bg-emerald-500',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.quizService
          .updateQuiz(quiz.id, {
            ...quiz,
            quizStatus: QuizStatus.completed,
          } as Quiz)
          .then(() => {
            this.logger.debug('Quiz completed successfully');
            this.toastrService.success(
              `Quiz "${quiz.title}" closed successfully`,
            );
            this.refreshQuizzes();
          })
          .catch((error) => {
            this.logger.error('Error completing quiz:', error);
            this.toastrService.error('Failed to close quiz');
          });
      });
  }

  async onDeleteQuiz(quiz: Quiz) {
    this.utilsService
      .onRequestConfirmation({
        title: 'Delete Quiz',
        description: `Are you sure you want to delete the quiz "${quiz.title}"? This action will permanently remove the quiz and all associated attempts/data. This cannot be undone.`,
        action: 'Delete',
        showType: AlertType.warning,
        buttonColor: 'bg-red-500',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.quizService
          .deleteQuiz(quiz.groupId, quiz.id)
          .then(() => {
            this.logger.debug('Quiz deleted successfully');
            this.toastrService.success(
              `Quiz "${quiz.title}" deleted successfully`,
            );
            this.refreshQuizzes();
          })
          .catch((error) => {
            this.logger.error('Error deleting quiz:', error);
            this.toastrService.error('Failed to delete quiz');
          });
      });
  }

  toggleDropdown(event: Event, quizId: string) {
    event.stopPropagation();
    if (this.activeDropdownQuizId() === quizId) {
      this.activeDropdownQuizId.set(null);
    } else {
      this.activeDropdownQuizId.set(quizId);
    }
  }

  @HostListener('document:click')
  closeDropdown() {
    this.activeDropdownQuizId.set(null);
  }

  copyInviteCode(inviteCode: string) {
    navigator.clipboard.writeText(inviteCode);
  }
}
